import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import SEO from "../components/SEO";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { COMPANY } from "../lib/constants";

const faqs = [
  {
    q: "Do you accept Medicaid?",
    a: "Yes. 1st Home Care Services is an approved Virginia Medicaid provider. Our team will help verify your benefits and get care started quickly.",
  },
  {
    q: "How fast can care start?",
    a: "Most families have care in place within 24–48 hours of their first call. We schedule a complimentary intake right away and match you with the right caregiver.",
  },
  {
    q: "Do you offer 24/7 care?",
    a: "Yes. We provide around-the-clock care, including overnight, weekend, and live-in coverage when needed.",
  },
  {
    q: "Where do you provide services?",
    a: "We serve families across Northern Virginia, including Springfield, Fairfax, Arlington, Alexandria, and Falls Church. Not sure if your area is covered? Just give us a call.",
  },
  {
    q: "What types of services do you offer?",
    a: "We offer skilled nursing, private duty nursing, personal care (bathing, grooming, mobility, meal prep, companionship), and respite care for family caregivers.",
  },
  {
    q: "Are your caregivers trained and vetted?",
    a: "Absolutely. All caregivers are background-checked, trained, and supervised by our experienced Director of Nursing.",
  },
  {
    q: "Do you accept private pay?",
    a: "Yes. We offer flexible private pay arrangements alongside Medicaid coverage so families can get the right level of support.",
  },
];

export default function FAQ() {
  return (
    <>
      <SEO
        title="FAQ | 1st Home Care Services — Home Care in Northern Virginia"
        description="Frequently asked questions about home care, Medicaid coverage, 24/7 care, and how to get started with 1st Home Care Services in Springfield, VA."
        canonical="https://1sthomecare.com/faq"
      />

      <section className="container-pad section-pad">
        <div className="max-w-3xl">
          <div className="text-xs font-bold uppercase tracking-widest text-brand-amber mb-3">
            Frequently Asked Questions
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-brand-blue-dark text-balance">
            Answers to common home care questions
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Don't see your question? Call us at{" "}
            <a href={`tel:${COMPANY.phoneTel}`} className="text-brand-blue font-semibold">
              {COMPANY.phone}
            </a>{" "}
            — we're happy to help.
          </p>
        </div>
      </section>

      <section className="container-pad pb-16 md:pb-24">
        <div className="max-w-3xl">
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {faqs.map((f, idx) => (
              <AccordionItem
                key={f.q}
                value={`item-${idx}`}
                data-testid={`faq-item-${idx}`}
                className="border-b border-gray-200"
              >
                <AccordionTrigger className="text-left font-heading text-lg font-semibold text-brand-blue-dark hover:text-brand-blue py-5">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bg-brand-gray/40 section-pad">
        <div className="container-pad text-center">
          <h2 className="font-heading text-3xl font-semibold text-brand-blue-dark">
            Still have questions?
          </h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a
              href={`tel:${COMPANY.phoneTel}`}
              data-testid="faq-call-now"
              className="inline-flex items-center gap-2 bg-brand-blue text-white font-semibold px-7 py-4 rounded-full"
            >
              <Phone className="w-4 h-4" /> Call {COMPANY.phone}
            </a>
            <Link
              to="/contact"
              data-testid="faq-contact"
              className="inline-flex items-center gap-2 bg-brand-amber text-white font-semibold px-7 py-4 rounded-full"
            >
              Get Care Online
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
